<template>
    <div class="media-preview bg-white border-t lg:border-t-0 lg:border-l border-gray-200 w-full lg:w-96 lg:flex-shrink-0 lg:h-full overflow-y-auto">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <!-- Mobile: back to the grid (detail is full-screen on small viewports) -->
            <button
                class="lg:hidden flex items-center gap-1.5 flex-shrink-0 mr-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                @click="$emit('close')"
            >
                <i class="far fa-arrow-left"></i> Terug
            </button>
            <h6 class="text-sm font-semibold text-gray-700 m-0 truncate pr-2 flex-1">{{ detail ? detail.name : item.name }}</h6>
            <div class="flex items-center gap-1 flex-shrink-0">
                <!-- Delete sits up here (away from the Gebruiken button); it has its own confirm.
                     Disabled when the media is in use — deleting would break a live <img>. -->
                <button
                    v-if="picker"
                    @click="deleteItem"
                    :disabled="!canDelete"
                    :title="canDelete ? 'Verwijderen' : deleteBlockedReason"
                    class="w-7 h-7 flex items-center justify-center rounded-md transition-colors"
                    :class="canDelete ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-300 cursor-not-allowed'"
                >
                    <i class="far fa-trash-alt"></i>
                </button>
                <button @click="$emit('close')" class="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                    <i class="far fa-times"></i>
                </button>
            </div>
        </div>

        <!-- Preview -->
        <div class="p-4">
            <div class="mb-4 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center" style="min-height: 200px;">
                <img
                    v-if="item.type === 'image' && item.url && !imageFailed"
                    :src="item.url"
                    :alt="item.name"
                    class="max-w-full max-h-64 object-contain"
                    @error="imageFailed = true"
                />
                <video
                    v-else-if="item.type === 'video' && item.url"
                    :src="item.url"
                    controls
                    class="max-w-full max-h-64"
                ></video>
                <audio
                    v-else-if="item.type === 'audio' && item.url"
                    :src="item.url"
                    controls
                    class="w-full"
                ></audio>
                <div v-else class="py-8 text-center text-gray-300">
                    <i :class="getTypeIcon(item.type)" class="text-5xl block mb-2"></i>
                    <span class="text-sm text-gray-400">{{ item.mime_type }}</span>
                </div>
            </div>

            <!-- Source badge -->
            <div class="mb-3">
                <span
                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    :class="item.source === 'spatie' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'"
                >
                    <i :class="item.source === 'spatie' ? 'far fa-link mr-1' : 'far fa-upload mr-1'" style="font-size: 10px;"></i>
                    {{ item.source === 'spatie' ? 'Gekoppeld aan content' : 'Handmatig geüpload' }}
                </span>
            </div>

            <!-- Metadata -->
            <div class="space-y-2 mb-4">
                <div class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Bestandsnaam</span>
                    <span class="text-gray-900 break-all">{{ item.file_name }}</span>
                </div>
                <div class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Type</span>
                    <span class="text-gray-900">{{ item.mime_type }}</span>
                </div>
                <div class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Grootte</span>
                    <span class="text-gray-900">{{ formatSize(item.size) }}</span>
                </div>
                <div v-if="item.dimensions" class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Afmetingen</span>
                    <span class="text-gray-900">{{ item.dimensions.width }} &times; {{ item.dimensions.height }} px</span>
                </div>
                <div class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Datum</span>
                    <span class="text-gray-900">{{ formatDate(item.created_at) }}</span>
                </div>
                <div v-if="item.collection" class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Collectie</span>
                    <span class="text-gray-900">{{ item.collection }}</span>
                </div>
                <div v-if="item.model_type" class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Model</span>
                    <span class="text-gray-900">{{ shortModelName(item.model_type) }} #{{ item.model_id }}</span>
                </div>
                <div v-if="item.directory" class="flex items-start text-sm">
                    <span class="text-gray-500 w-28 flex-shrink-0">Map</span>
                    <span class="text-gray-900">{{ item.directory }}</span>
                </div>
            </div>

            <!-- URL -->
            <div class="mb-4">
                <label class="text-xs text-gray-500 font-medium mb-1.5 block">URL</label>
                <div class="flex rounded-md shadow-sm">
                    <input
                        type="text"
                        :value="item.url"
                        readonly
                        class="flex-1 min-w-0 block rounded-none rounded-l-md border-gray-300 bg-gray-50 text-xs text-gray-600 focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <button
                        @click="copyUrl"
                        title="Kopieer URL"
                        class="-ml-px relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        <i class="far fa-copy"></i>
                    </button>
                </div>
            </div>

            <!-- Usage -->
            <media-usage-list
                v-if="detail && detail.usage"
                :usages="detail.usage"
                :usage-count="item.usage_count"
            ></media-usage-list>
            <div v-else-if="loadingDetail" class="text-center py-4">
                <i class="far fa-circle-notch fa-spin text-gray-300"></i>
            </div>

            <!-- Why delete is unavailable (in use somewhere). -->
            <p v-if="!canDelete" class="mt-4 text-xs flex items-start bg-indigo-100 text-indigo-700 px-4 py-2.5 rounded-md gap-1.5">
                <i class="far fa-lock mt-0.5"></i>
                <span>{{ deleteBlockedReason }}</span>
            </p>

            <!-- Actions — pinned to the bottom so they stay in view while scrolling -->
            <div class="sticky bottom-0 -mx-4 mt-4 px-4 pt-4 pb-4 border-t border-gray-200 bg-white flex gap-2">
                <a
                    :href="item.url"
                    target="_blank"
                    class="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <i class="far fa-external-link mr-1.5"></i> Openen
                </a>
                <button
                    v-if="picker"
                    @click="$emit('select', item)"
                    class="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <i class="far fa-check mr-1.5"></i> Gebruiken
                </button>
                <button
                    v-else
                    @click="deleteItem"
                    :disabled="!canDelete"
                    :title="canDelete ? '' : deleteBlockedReason"
                    class="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    :class="canDelete ? 'text-red-700 bg-red-50 hover:bg-red-100' : 'text-gray-400 bg-gray-50 cursor-not-allowed'"
                >
                    <i class="far fa-trash-alt mr-1.5"></i> Verwijderen
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MediaPreview',

    props: {
        item: { type: Object, required: true },
        detailUrl: { type: String, required: true },
        deleteUrl: { type: String, required: true },
        picker: { type: Boolean, default: false },
    },

    data() {
        return {
            detail: null,
            loadingDetail: false,
            imageFailed: false,
            alt: '',
        };
    },

    computed: {
        // Warn when WCAG is on and there's no alt text (only meaningful in picker mode).
        altWarning() {
            return this.picker && !!window.galaxyWcag && !(this.alt && this.alt.trim());
        },
        // In use → not deletable (would break a live <img>). Default true so
        // items without the flag (older payloads) still behave as before.
        canDelete() {
            return this.item.deletable !== false;
        },
        deleteBlockedReason() {
            return this.item.source === 'lfm'
                ? 'Dit bestand wordt gebruikt op een pagina — verwijder of vervang het daar eerst.'
                : 'Deze afbeelding is gekoppeld aan content — verwijder of vervang het in de module of pagina.';
        },
    },

    watch: {
        'item.id': {
            immediate: true,
            handler: function () {
                this.imageFailed = false;
                this.alt = this.item.name || '';
                this.fetchDetail();
            },
        },
    },

    methods: {
        // Persist alt text — in Galaxy a media's alt is its name (Spatie media only).
        saveAlt() {
            var value = (this.alt || '').trim();

            if (value === (this.item.name || '')) {
                return; // unchanged
            }

            var self = this;
            var url = this.detailUrl + '/' + this.item.source + '/' + encodeURIComponent(this.item.source_id);

            axios.patch(url, { alt: value })
                .then(function () {
                    self.item.name = value; // keep the panel/grid label in sync
                    self.$emit('updated', { id: self.item.id, name: value });
                    Vue.toasted.show('Alt-tekst opgeslagen', { type: 'success' });
                })
                .catch(function () {
                    Vue.toasted.show('Opslaan mislukt', { type: 'error' });
                });
        },
        fetchDetail() {
            this.detail = null;
            this.loadingDetail = true;

            var url = this.detailUrl + '/' + this.item.source + '/' + encodeURIComponent(this.item.source_id);

            axios.get(url)
                .then(function (response) {
                    this.detail = response.data;
                    this.loadingDetail = false;
                }.bind(this))
                .catch(function () {
                    this.loadingDetail = false;
                }.bind(this));
        },

        copyUrl() {
            var self = this;

            if (navigator.clipboard) {
                navigator.clipboard.writeText(this.item.url).then(function () {
                    Vue.toasted.show('URL gekopieerd', { type: 'success' });
                });
            } else {
                var input = document.createElement('input');
                input.value = self.item.url;
                document.body.appendChild(input);
                input.select();
                document.execCommand('copy');
                document.body.removeChild(input);
                Vue.toasted.show('URL gekopieerd', { type: 'success' });
            }
        },

        deleteItem() {
            var self = this;

            if (!this.canDelete) {
                Vue.toasted.show(this.deleteBlockedReason, { type: 'info' });
                return;
            }

            Swal.fire({
                title: 'Weet je het zeker?',
                text: '"' + this.item.name + '" wordt permanent verwijderd.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                confirmButtonText: 'Verwijderen',
                cancelButtonText: 'Annuleren',
                // Render above the picker modal (z-index 100000).
                didOpen: function () {
                    var c = document.querySelector('.swal2-container');
                    if (c) { c.style.zIndex = '200001'; }
                },
            }).then(function (result) {
                if (!result.isConfirmed) return;

                var payload = {
                    items: [{ source: self.item.source, id: self.item.source_id }],
                };

                axios.delete(self.deleteUrl, { data: payload })
                    .then(function (response) {
                        var blocked = (response.data && response.data.blocked) || [];
                        if (blocked.length) {
                            var where = self.usageLabels(blocked[0].usages);
                            Vue.toasted.show('Kan niet verwijderen — in gebruik' + (where ? ' bij ' + where : '') + '.', { type: 'error' });
                            return;
                        }
                        self.$emit('deleted', self.item);
                        Vue.toasted.show('Bestand verwijderd', { type: 'success' });
                    });
            });
        },

        usageLabels(usages) {
            if (!usages || !usages.length) return '';
            return usages.map(function (u) { return u.label; }).filter(Boolean).slice(0, 3).join(', ');
        },

        getTypeIcon(type) {
            var icons = {
                image: 'far fa-image',
                video: 'far fa-video',
                audio: 'far fa-music',
                document: 'far fa-file-alt',
                archive: 'far fa-file-archive',
                other: 'far fa-file',
            };
            return icons[type] || 'far fa-file';
        },

        shortModelName(modelType) {
            if (!modelType) return '';
            var parts = modelType.split('\\');
            return parts[parts.length - 1];
        },

        formatSize(bytes) {
            if (!bytes) return '0 B';
            var units = ['B', 'KB', 'MB', 'GB'];
            var i = 0;
            var size = bytes;
            while (size >= 1024 && i < units.length - 1) {
                size /= 1024;
                i++;
            }
            return size.toFixed(1) + ' ' + units[i];
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            var d = new Date(dateStr);
            return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        },
    },
};
</script>
